import React, { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Mail, Globe, MapPin, Building2, Award, Home, ArrowRight, Volume2, VolumeX } from "lucide-react";
import { PlatformLayout, WhatsAppIcon } from "./PlatformLayout";
import { ListingCard } from "./ListingCard";
import { Reveal } from "./Reveal";
import { AnimatedTitle } from "./AnimatedTitle";
import { MediaBackground, parseVideo } from "./MediaBackground";
import { Prose } from "./Prose";
import { pickVisuals } from "./memberVisuals";
import { fetchMember, fetchConseillerDuMois } from "./api";

export default function MemberPage() {
  const { slug } = useParams();
  const [member, setMember] = useState(undefined);
  const [heroIdx, setHeroIdx] = useState(0);
  const [videoMuted, setVideoMuted] = useState(true);
  const [cdm, setCdm] = useState(null);

  useEffect(() => {
    let active = true;
    setMember(undefined);
    setHeroIdx(0);
    fetchMember(slug).then((d) => { if (active) setMember(d); });
    fetchConseillerDuMois().then((d) => { if (active) setCdm(d); });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    if (!member || member.hero_video) return;
    const g = member.gallery && member.gallery.length ? member.gallery : (member.photo ? [member.photo] : []);
    if (g.length <= 1) return;
    const t = setInterval(() => setHeroIdx((p) => (p + 1) % g.length), 5000);
    return () => clearInterval(t);
  }, [member]);

  if (member === undefined) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="member-loading">
        <div className="w-10 h-10 border-2 border-[#C19A6B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (member === null) return <Navigate to="/" replace />;

  const m = member;
  const listings = m.listings || [];
  const isCdm = !!(cdm && cdm.slug && m.slug && cdm.slug === m.slug);
  const waNumber = (m.phone || "").replace(/[^0-9]/g, "").replace(/^0/, "33");
  const stats = m.stats || {};
  const hasVideo = !!parseVideo(m.hero_video);
  const heroPhotos = m.gallery && m.gallery.length ? m.gallery : (m.photo ? [m.photo] : []);
  const bg = heroPhotos.length ? heroPhotos[heroIdx % heroPhotos.length] : null;
  const presImgs = pickVisuals(`${m.description || ""} ${m.sector || ""} ${m.city || ""} ${m.account_type || ""}`, 2);
  const visionImg = pickVisuals(`${m.vision || ""} ${m.off_market_presentation || ""}`, 1)[0];
  const galleryPhotos = (m.gallery || []).slice(0, 10);

  const contactBtns = (
    <div className="flex flex-wrap items-center gap-3 pt-1">
      {m.phone && <a href={`tel:${m.phone.replace(/\s/g, "")}`} data-testid="member-call" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-sm font-medium uppercase tracking-wider px-5 py-3 transition-colors"><Phone className="w-4 h-4" /> Appeler</a>}
      {waNumber && <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer" data-testid="member-whatsapp" className="inline-flex items-center gap-2 bg-black hover:bg-[#C19A6B] text-white text-sm font-medium uppercase tracking-wider px-5 py-3 border border-white/15 transition-colors"><WhatsAppIcon className="w-4 h-4" /> WhatsApp</a>}
      {m.email && <a href={`mailto:${m.email}`} data-testid="member-email" className="inline-flex items-center gap-2 border border-white/15 hover:border-[#C19A6B] text-zinc-200 hover:text-[#C19A6B] text-sm font-medium uppercase tracking-wider px-5 py-3 transition-colors"><Mail className="w-4 h-4" /> E-mail</a>}
      {m.website && <a href={m.website.startsWith("http") ? m.website : `https://${m.website}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-white/15 hover:border-[#C19A6B] text-zinc-200 hover:text-[#C19A6B] text-sm font-medium uppercase tracking-wider px-5 py-3 transition-colors"><Globe className="w-4 h-4" /> Site</a>}
      {hasVideo && (
        <button type="button" data-testid="member-sound-toggle" onClick={() => setVideoMuted((mm) => !mm)} aria-label={videoMuted ? "Activer le son" : "Couper le son"} className="inline-flex items-center gap-2 border border-white/15 hover:border-[#C19A6B] text-zinc-200 hover:text-[#C19A6B] text-sm font-medium uppercase tracking-wider px-5 py-3 transition-colors">
          {videoMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />} {videoMuted ? "Son" : "Couper"}
        </button>
      )}
    </div>
  );

  return (
    <PlatformLayout searchDock={false}>
      <div className="platform-scope bg-[#050505] text-white min-h-screen pb-24" data-testid="member-page">
        {/* Hero conseiller — média 16:9 plein écran, menu en transparence par-dessus (vidéo prioritaire) */}
        <section className="w-full">
          <div className="relative w-full h-screen max-h-[100vh] min-h-[560px] overflow-hidden bg-[#0d0d10]" data-testid="member-hero">
            {hasVideo ? (
              <MediaBackground videoUrl={m.hero_video} start={m.hero_video_start} end={m.hero_video_end} cover={false} muted={videoMuted} />
            ) : bg ? (
              <div className="absolute inset-0 bg-cover bg-center transition-all duration-1000" style={{ backgroundImage: `url(${bg})` }} data-testid="member-hero-photo" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center"><Building2 className="w-20 h-20 text-[#C19A6B]/30" /></div>
            )}
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(5,5,5,0.94) 0%, rgba(5,5,5,0.35) 48%, rgba(5,5,5,0.08) 100%)" }} />

            <div className="absolute inset-0 flex items-end">
              <div className="w-full p-6 sm:p-10 flex flex-col sm:flex-row sm:items-end gap-5">
                {!hasVideo && (
                  <div data-testid="member-hero-avatar" className="w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden border-2 border-[#C19A6B] bg-white/5 flex items-center justify-center shrink-0">
                    {m.photo ? <img src={m.photo} alt={m.display_name} className="w-full h-full object-cover" /> : <Building2 className="w-12 h-12 text-[#C19A6B]/50" />}
                  </div>
                )}
                <div className="flex-1 space-y-3">
                  {isCdm && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#C19A6B] text-white text-[11px] uppercase tracking-wider font-bold shadow-lg" data-testid="conseiller-du-mois-badge"><Award className="w-3.5 h-3.5" /> Conseiller du mois</span>
                  )}
                  <AnimatedTitle as="h1" className="text-4xl sm:text-5xl font-light tracking-tight" text={m.display_name} />
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-zinc-200">
                    {m.account_type && <span className="flex items-center gap-1.5"><Award className="w-4 h-4 text-[#C19A6B]" /> {m.account_type}</span>}
                    {m.company && <span className="flex items-center gap-1.5"><Building2 className="w-4 h-4 text-[#C19A6B]" /> {m.company}</span>}
                    {(m.city || m.sector) && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#C19A6B]" /> {m.sector || m.city}</span>}
                  </div>
                  {contactBtns}
                </div>
                {m.logo && <img src={m.logo} alt={m.company} className="h-20 object-contain self-start sm:self-end hidden sm:block" />}
              </div>
            </div>
          </div>
        </section>

        {/* Présentation — texte centré + belles photos correspondant au profil */}
        {m.description && (
          <section className="max-w-4xl mx-auto px-6 lg:px-12 mt-16 text-center" data-testid="member-presentation">
            <Reveal>
              <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">À propos de :</span>
              <AnimatedTitle as="h2" className="text-3xl sm:text-4xl font-light tracking-tight mt-2 mb-6" text={m.display_name} />
              <Prose text={m.description} className="text-zinc-300 font-light leading-relaxed text-base sm:text-lg" pClassName="mx-auto max-w-3xl" />
            </Reveal>
          </section>
        )}

        {/* Stats */}
        <section className="max-w-6xl mx-auto px-6 lg:px-12 mt-14">
          <div className="grid grid-cols-3 gap-4">
            {[
              { v: stats.listings ?? listings.length, l: "Biens en portefeuille" },
              { v: stats.years_experience ? `${stats.years_experience}+` : "—", l: "Années d'expérience" },
              { v: stats.sales || "—", l: "Ventes réalisées" },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 0.1} className="border border-white/10 bg-[#0d0d10] p-6 text-center">
                <p className="text-3xl font-light text-[#C19A6B]">{s.v}</p>
                <p className="text-xs uppercase tracking-wider text-zinc-500 mt-1">{s.l}</p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Vision — texte centré sur belle photo d'ambiance */}
        {m.vision && (
          <section className="relative mt-20 py-20 overflow-hidden" data-testid="member-vision">
            {visionImg && (
              <div className="absolute inset-0">
                <img src={visionImg.url} alt={visionImg.alt} className="w-full h-full object-cover opacity-20" />
                <div className="absolute inset-0 bg-[#050505]/82" />
              </div>
            )}
            <div className="relative max-w-3xl mx-auto px-6 text-center">
              <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">Sa vision du métier</span>
              <Prose text={m.vision} className="text-xl sm:text-2xl font-light text-zinc-100 leading-relaxed mt-5 italic" pClassName="mx-auto max-w-2xl" />
            </div>
          </section>
        )}

        {/* L'off-market */}
        {m.off_market_presentation && (
          <section className="max-w-3xl mx-auto px-6 lg:px-12 mt-16 text-center" data-testid="member-offmarket">
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-zinc-500">L'off-market</span>
            <Prose text={m.off_market_presentation} className="text-zinc-400 font-light leading-relaxed mt-4" pClassName="mx-auto max-w-2xl" />
          </section>
        )}

        {/* Galerie photos — masonry (max 10) */}
        {galleryPhotos.length > 0 && (
          <section className="max-w-6xl mx-auto px-6 lg:px-12 mt-20" data-testid="member-gallery">
            <Reveal className="text-center mb-8">
              <span className="calli text-lg text-[#C19A6B]">En images</span>
              <AnimatedTitle as="h2" className="text-3xl font-light tracking-tight mt-1" text="La galerie" />
            </Reveal>
            <div className="columns-2 md:columns-3 gap-4 [column-fill:_balance]">
              {galleryPhotos.map((src, i) => (
                <Reveal key={i} delay={(i % 3) * 0.08} className="mb-4 break-inside-avoid">
                  <div className="relative overflow-hidden border border-white/10 group">
                    <img src={src} alt={`${m.display_name} — photo ${i + 1}`} loading="lazy" className="w-full h-auto object-cover group-hover:scale-[1.04] transition-transform duration-1000" data-testid="member-gallery-photo" />
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {/* Annonces du conseiller */}
        <div className="bg-zinc-100 mt-20 py-16">
        <section className="max-w-[1500px] mx-auto px-6 lg:px-12">
          <Reveal className="mb-8 flex items-end justify-between">
            <div>
              <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">Son portefeuille</span>
            </div>
            <Link to="/biens-off-market" className="hidden sm:inline-flex items-center gap-2 text-sm text-[#C19A6B] hover:gap-3 transition-all">Tous les biens <ArrowRight className="w-4 h-4" /></Link>
          </Reveal>
          {listings.length === 0 ? (
            <div className="py-16 text-center text-zinc-500 border border-dashed border-zinc-300" data-testid="member-no-listings">
              <Home className="w-8 h-8 mx-auto mb-3 text-zinc-400" />
              Aucun bien disponible pour le moment.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="member-listings-grid">
              {listings.map((l, i) => <ListingCard key={l.slug} listing={{ ...l, owner: { display_name: m.display_name, company: m.company, logo: m.logo, name: m.display_name, slug: m.slug } }} index={i} />)}
            </div>
          )}
        </section>
        </div>
      </div>
    </PlatformLayout>
  );
}
